import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, AlertCircle, Save, History, X } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function lastDay(y, m) { return new Date(y, m, 0).getDate(); }
function fmtDate(y, m, d) {
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

export default function PaySlipOverviewPage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const isMonthly = (user?.salary_frequency || "twice_monthly") === "monthly";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [period, setPeriod] = useState(isMonthly ? "monthly" : "second_half");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSnap, setSavingSnap] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const saveSnapshot = async () => {
    if (!data || (data.rows || []).length === 0) {
      toast.error("Nothing to snapshot — load the overview first");
      return;
    }
    setSavingSnap(true);
    try {
      await api.post("/payslip/overview/save", {
        branch_id: selectedBranch.id,
        year, month, period,
        snapshot: data,
        note: `${MONTHS[month - 1]} ${year} · ${period.replace("_", " ")}`,
      });
      toast.success("Snapshot saved — visit View History to review");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSnap(false); }
  };

  useEffect(() => {
    if (isMonthly && period !== "monthly") setPeriod("monthly");
    if (!isMonthly && period === "monthly") setPeriod("second_half");
  }, [isMonthly, period]);

  const load = useCallback(async () => {
    if (!selectedBranch) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/payslip/overview", {
        params: { branch_id: selectedBranch.id, year, month, period },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedBranch, year, month, period]);

  useEffect(() => { load(); }, [load]);

  const coveredDates = useMemo(() => {
    if (period === "first_half") return `${fmtDate(year, month, 1)} to ${fmtDate(year, month, 15)}`;
    if (period === "second_half") return `${fmtDate(year, month, 16)} to ${fmtDate(year, month, lastDay(year, month))}`;
    return `${fmtDate(year, month, 1)} to ${fmtDate(year, month, lastDay(year, month))}`;
  }, [year, month, period]);

  const yearOptions = useMemo(() => {
    const y = today.getFullYear(); const out = [];
    for (let i = y - 3; i <= y + 1; i++) out.push(i);
    return out;
  }, [today]);

  // Amount formatter — keep the currency symbol and the number on the same line
  // (no whitespace between them) so they don't wrap inside narrow print cells.
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Grand totals across all employees, mirroring the row schema
  const totals = useMemo(() => {
    if (!data) return null;
    const t = {
      by_label: {},          // label_id -> number OR {normal, reg, spec, subtotal}
      overtime_total: 0,
      by_deduction: {},
      by_other_deduction: {},
      other_deductions_total: 0,
      gross_pay: 0, net_pay: 0,
    };
    for (const col of data.income_columns) {
      t.by_label[col.id] = col.has_breakdown
        ? { normal_working_day: 0, regular_holiday: 0, special_holiday: 0, subtotal: 0 }
        : 0;
    }
    for (const r of data.rows) {
      for (const col of data.income_columns) {
        const v = r.by_label[col.id];
        if (col.has_breakdown) {
          const cur = t.by_label[col.id];
          cur.normal_working_day += (v?.normal_working_day || 0);
          cur.regular_holiday += (v?.regular_holiday || 0);
          cur.special_holiday += (v?.special_holiday || 0);
          cur.subtotal += (v?.subtotal || 0);
        } else {
          t.by_label[col.id] = (t.by_label[col.id] || 0) + (v || 0);
        }
      }
      t.overtime_total += r.overtime_total || 0;
      for (const l of data.deduction_labels) t.by_deduction[l.id] = (t.by_deduction[l.id] || 0) + (r.by_deduction[l.id] || 0);
      for (const n of data.other_deduction_names) t.by_other_deduction[n] = (t.by_other_deduction[n] || 0) + (r.by_other_deduction?.[n] || 0);
      t.other_deductions_total += r.other_deductions_total || 0;
      t.gross_pay += r.gross_pay || 0;
      t.net_pay += r.net_pay || 0;
    }
    return t;
  }, [data]);

  // Total table columns = name + (per-income: 3 sub-cols if breakdown AND first
  //                       breakdown column, else 1) + overtime + per-deduction
  //                       + per-other-ded-name + gross + net
  // Only the first income column with `has_breakdown` keeps its day-type split;
  // every subsequent breakdown column is consolidated into a single Amount
  // column showing the subtotal (per user request for both screen + print).
  const firstBreakdownColId = useMemo(
    () => data?.income_columns?.find((c) => c.has_breakdown)?.id || null,
    [data]
  );
  const isExpanded = (col) => col.has_breakdown && col.id === firstBreakdownColId;
  const incomeColSpan = (col) => (isExpanded(col) ? 3 : 1);
  // For consolidated (collapsed) income columns we render their subtotal as a
  // single Amount value.
  const incomeCellValue = (col, v) => (isExpanded(col)
    ? v // caller handles the breakdown sub-cells
    : (col.has_breakdown ? (v?.subtotal || 0) : v));

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Pay Slip Overview</h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm">Summary of every employee's pay slip for the selected period.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/employees/payslip-history">
            <Button variant="outline"
              className="rounded-2xl border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] shadow-sm h-11 px-5" data-testid="view-history-link">
              <History className="w-4 h-4 mr-1.5" /> View History
            </Button>
          </Link>
          <Button onClick={saveSnapshot} disabled={!data || data.rows?.length === 0 || savingSnap}
            className="rounded-2xl bg-gradient-to-r from-[#2E7D32] to-[#43A047] hover:from-[#1B5E20] hover:to-[#2E7D32] text-white shadow-sm h-11 px-5" data-testid="save-overview-btn">
            <Save className="w-4 h-4 mr-1.5" /> {savingSnap ? "Saving..." : "Save Snapshot"}
          </Button>
          <Button onClick={() => setPreviewOpen(true)} disabled={!data || data.rows.length === 0}
            className="rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white shadow-sm h-11 px-5" data-testid="print-overview-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print Overview
          </Button>
        </div>
      </div>

      {!selectedBranch ? <NoBranchCard /> : (
        <>
          {/* Filter bar */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4">
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Branch">
                <select value={selectedBranch.id} onChange={(e) => setSelectedBranchId(e.target.value)} className="ovw-select" data-testid="branch-select">
                  {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Month">
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="ovw-select" data-testid="month-select">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Pay period">
                {isMonthly ? (
                  <div className="ovw-select flex items-center">Monthly</div>
                ) : (
                  <select value={period} onChange={(e) => setPeriod(e.target.value)} className="ovw-select" data-testid="period-select">
                    <option value="first_half">1st Half (1–15)</option>
                    <option value="second_half">2nd Half (16–End)</option>
                  </select>
                )}
              </Field>
              <Field label="Year">
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ovw-select" data-testid="year-select">
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
            <p className="text-sm text-brand-inkMuted mt-3">
              Covered Dates: <span className="font-heading font-semibold text-foreground">{coveredDates}</span>
            </p>
          </section>

          {loading ? <p className="text-brand-inkMuted">Loading...</p>
            : !data || data.rows.length === 0 ? (
              <div className="bg-white rounded-2xl border border-brand-purple/10 p-10 text-center" data-testid="overview-empty">
                <p className="font-heading font-semibold text-foreground">No employees in this branch yet.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm ovw-scroll" data-testid="overview-table">
                <table className="ovw-table text-xs sm:text-sm border-collapse">
                  <thead className="bg-gradient-to-b from-[#EEF3FF] to-[#F6F8FE] text-brand-inkMuted">
                    {/* Group header row */}
                    <tr>
                      <th rowSpan={2} className="ovw-th-name">EMPLOYEE NAME</th>
                      {data.income_columns.map((col) => (
                        <th
                          key={col.id}
                          colSpan={incomeColSpan(col)}
                          className="ovw-th-group ovw-th-income"
                        >
                          <div className="ovw-group-title">{col.name?.toUpperCase()}</div>
                          <div className="ovw-group-sub">{col.schedule}</div>
                        </th>
                      ))}
                      <th rowSpan={2} className="ovw-th-leaf ovw-th-income">
                        <div className="ovw-group-title">OVERTIME PAY</div>
                        <div className="ovw-group-sub">Across all daily columns</div>
                      </th>
                      {data.deduction_labels.length > 0 && (
                        <th colSpan={data.deduction_labels.length} className="ovw-th-group ovw-th-gov">
                          <div className="ovw-group-title">GOVERNMENT DEDUCTIONS</div>
                          <div className="ovw-group-sub">Apply this period</div>
                        </th>
                      )}
                      {data.other_deduction_names.length > 0 && (
                        <th colSpan={data.other_deduction_names.length} className="ovw-th-group ovw-th-other">
                          <div className="ovw-group-title">OTHER DEDUCTIONS</div>
                          <div className="ovw-group-sub">Salary-deducted</div>
                        </th>
                      )}
                      <th rowSpan={2} className="ovw-th-leaf ovw-th-gross">
                        <div className="ovw-group-title">GROSS PAY</div>
                        <div className="ovw-group-sub">Total</div>
                      </th>
                      <th rowSpan={2} className="ovw-th-leaf ovw-th-net">
                        <div className="ovw-group-title">NET PAY</div>
                        <div className="ovw-group-sub">Take home</div>
                      </th>
                    </tr>
                    {/* Sub-header row */}
                    <tr>
                      {data.income_columns.map((col) => (
                        isExpanded(col) ? (
                          <React.Fragment key={`${col.id}-sub`}>
                            <th className="ovw-th-sub">Normal Working Day</th>
                            <th className="ovw-th-sub">Regular Holiday</th>
                            <th className="ovw-th-sub">Special Holiday</th>
                          </React.Fragment>
                        ) : (
                          <th key={`${col.id}-sub`} className="ovw-th-sub">Amount</th>
                        )
                      ))}
                      {data.deduction_labels.map((l) => (
                        <th key={l.id} className="ovw-th-sub">{l.name?.toUpperCase()}</th>
                      ))}
                      {data.other_deduction_names.map((n) => (
                        <th key={n} className="ovw-th-sub">{n?.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={r.employee_id} className={`ovw-row ${i % 2 ? "ovw-row-odd" : "ovw-row-even"}`} data-testid={`overview-row-${r.employee_id}`}>
                        <td className="ovw-td-name">{r.employee_name}</td>
                        {data.income_columns.map((col) => {
                          const v = r.by_label[col.id];
                          if (isExpanded(col)) {
                            return (
                              <React.Fragment key={`${r.employee_id}-${col.id}`}>
                                <td className="ovw-td">{fmt(v?.normal_working_day)}</td>
                                <td className="ovw-td">{fmt(v?.regular_holiday)}</td>
                                <td className="ovw-td">{fmt(v?.special_holiday)}</td>
                              </React.Fragment>
                            );
                          }
                          return (
                            <td key={`${r.employee_id}-${col.id}`} className="ovw-td">{fmt(incomeCellValue(col, v))}</td>
                          );
                        })}
                        <td className="ovw-td ovw-td-accent">{fmt(r.overtime_total)}</td>
                        {data.deduction_labels.map((l) => (
                          <td key={l.id} className="ovw-td">{fmt(r.by_deduction[l.id])}</td>
                        ))}
                        {data.other_deduction_names.map((n) => (
                          <td key={n} className="ovw-td">{fmt(r.by_other_deduction?.[n])}</td>
                        ))}
                        <td className="ovw-td ovw-td-gross">{fmt(r.gross_pay)}</td>
                        <td className="ovw-td ovw-td-net">{fmt(r.net_pay)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {totals && (
                      <tr className="ovw-foot" data-testid="overview-grand-total">
                        <td className="ovw-td-name ovw-td-foot">Grand Total</td>
                        {data.income_columns.map((col) => {
                          const v = totals.by_label[col.id];
                          if (isExpanded(col)) {
                            return (
                              <React.Fragment key={`tot-${col.id}`}>
                                <td className="ovw-td ovw-td-foot">{fmt(v?.normal_working_day)}</td>
                                <td className="ovw-td ovw-td-foot">{fmt(v?.regular_holiday)}</td>
                                <td className="ovw-td ovw-td-foot">{fmt(v?.special_holiday)}</td>
                              </React.Fragment>
                            );
                          }
                          return <td key={`tot-${col.id}`} className="ovw-td ovw-td-foot">{fmt(incomeCellValue(col, v))}</td>;
                        })}
                        <td className="ovw-td ovw-td-foot ovw-td-accent">{fmt(totals.overtime_total)}</td>
                        {data.deduction_labels.map((l) => (
                          <td key={l.id} className="ovw-td ovw-td-foot">{fmt(totals.by_deduction[l.id])}</td>
                        ))}
                        {data.other_deduction_names.map((n) => (
                          <td key={n} className="ovw-td ovw-td-foot">{fmt(totals.by_other_deduction[n])}</td>
                        ))}
                        <td className="ovw-td ovw-td-foot ovw-td-gross">{fmt(totals.gross_pay)}</td>
                        <td className="ovw-td ovw-td-foot ovw-td-net">{fmt(totals.net_pay)}</td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            )}
        </>
      )}

      {previewOpen && data && (
        <PrintPreviewOverlay
          school={selectedSchool}
          branch={selectedBranch}
          user={user}
          coveredDates={coveredDates}
          period={period}
          year={year}
          month={month}
          data={data}
          totals={totals}
          incomeColSpan={incomeColSpan}
          isExpanded={isExpanded}
          incomeCellValue={incomeCellValue}
          fmt={fmt}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      <style>{`
        .ovw-select {
          width: 100%; height: 2.5rem; border-radius: 0.875rem;
          border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
          padding: 0 0.625rem; font-size: 0.875rem; color: hsl(var(--foreground));
        }
        .ovw-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }

        /* --- Pay Slip Overview table --- */
        .ovw-scroll {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
          scrollbar-color: #2563eb #E6ECF8;
        }
        .ovw-scroll::-webkit-scrollbar { height: 12px; }
        .ovw-scroll::-webkit-scrollbar-track {
          background: #EEF3FF;
          border-radius: 0 0 1rem 1rem;
        }
        .ovw-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #2563eb, #4f7cf5);
          border-radius: 999px;
          border: 2px solid #EEF3FF;
        }
        .ovw-scroll::-webkit-scrollbar-thumb:hover { background: #1d4ed8; }

        .ovw-table {
          width: max-content;
          min-width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        /* Group / leaf header cells */
        .ovw-th-group, .ovw-th-leaf, .ovw-th-name, .ovw-th-sub {
          font-family: var(--font-heading), inherit;
          font-weight: 700;
          letter-spacing: 0.04em;
          white-space: nowrap;
        }
        .ovw-th-group {
          text-align: center;
          padding: 0.625rem 0.875rem;
          font-size: 11px;
          line-height: 1.2;
          border-left: 1px solid rgba(37,99,235,0.10);
          border-bottom: 1px solid rgba(37,99,235,0.10);
        }
        .ovw-th-leaf {
          text-align: center;
          padding: 0.75rem 0.875rem;
          font-size: 11px;
          line-height: 1.2;
          border-left: 1px solid rgba(37,99,235,0.10);
          border-bottom: 1px solid rgba(37,99,235,0.10);
        }
        .ovw-th-income { color: #047857; }
        .ovw-th-gov { color: #b91c1c; }
        .ovw-th-other { color: #2563eb; }
        .ovw-th-gross { color: #0f172a; }
        .ovw-th-net { color: #2563eb; }
        .ovw-group-title { font-size: 11px; }
        .ovw-group-sub {
          margin-top: 2px;
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0;
          color: #94a3b8;
          text-transform: none;
        }

        .ovw-th-name {
          text-align: left;
          padding: 0.625rem 1rem;
          font-size: 11px;
          color: #475569;
          background: linear-gradient(180deg, #EEF3FF, #F6F8FE);
          position: sticky; left: 0; z-index: 3;
          border-right: 1px solid rgba(37,99,235,0.12);
          border-bottom: 1px solid rgba(37,99,235,0.10);
          min-width: 200px;
          box-shadow: 6px 0 14px -10px rgba(15,23,42,0.18);
        }

        .ovw-th-sub {
          text-align: center;
          padding: 0.5rem 0.875rem;
          font-size: 10px;
          color: #64748b;
          background: #F6F8FE;
          border-left: 1px solid rgba(37,99,235,0.08);
          border-bottom: 1px solid rgba(37,99,235,0.10);
          min-width: 110px;
        }

        /* Body */
        .ovw-row td {
          border-bottom: 1px solid rgba(37,99,235,0.06);
          transition: background-color 0.15s ease;
        }
        .ovw-row-even { background: #FAFBFE; }
        .ovw-row-odd { background: #FFFFFF; }
        .ovw-row:hover td { background: #F1F5FF; }
        .ovw-row:hover .ovw-td-name { background: #F1F5FF; }

        .ovw-td {
          text-align: center;
          padding: 0.875rem 0.875rem;
          font-size: 13px;
          color: #1e293b;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
          border-left: 1px solid rgba(37,99,235,0.05);
          min-width: 110px;
        }
        .ovw-td-accent { color: #047857; font-weight: 600; }
        .ovw-td-gross { font-weight: 700; color: #0f172a; }
        .ovw-td-net { font-weight: 700; color: #2563eb; }

        .ovw-td-name {
          text-align: left;
          padding: 0.875rem 1rem;
          font-family: var(--font-heading), inherit;
          font-weight: 600;
          font-size: 12.5px;
          letter-spacing: 0.02em;
          color: #0f172a;
          text-transform: uppercase;
          white-space: nowrap;
          position: sticky; left: 0; z-index: 2;
          background: inherit;
          border-right: 1px solid rgba(37,99,235,0.08);
          box-shadow: 6px 0 14px -10px rgba(15,23,42,0.18);
          min-width: 200px;
        }

        /* Footer (grand total) */
        .ovw-foot td {
          background: linear-gradient(180deg, #EEF3FF, #E2EAFB);
          font-family: var(--font-heading), inherit;
          font-weight: 700;
          font-size: 13px;
          color: #0f172a;
          border-top: 1.5px solid rgba(37,99,235,0.25);
          border-bottom: none;
        }
        .ovw-td-foot { padding-top: 1rem; padding-bottom: 1rem; }
      `}</style>
    </>
  );
}

function PrintPreviewOverlay({
  school, branch, user, coveredDates, period, year, month,
  data, totals, incomeColSpan, isExpanded, incomeCellValue, fmt, onClose,
}) {
  const periodLabel =
    period === "first_half" ? "1st Half (1–15)" :
    period === "second_half" ? "2nd Half (16–End)" : "Monthly";
  const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][month - 1];

  // Signature names — when an owner is printing they sign the right side; the
  // branch admin on the left is left blank for a wet signature. When a branch
  // admin is printing they sign the left side. We always show the school owner
  // (resolved via /schools/mine -> owner_name).
  const isOwnerPrinting = user?.role === "school_owner" || user?.role === "sub_owner";
  // The lower-left "BRANCH ADMIN" signature must reflect the actual assigned
  // branch admin (managed via the Branch Admins page), not the user who's
  // printing. Fallback chain: branch.branch_admin_name → current user only if
  // THEY are a branch_admin → blank line for a handwritten signature.
  const branchAdminName =
    branch?.branch_admin_name ||
    (user?.role === "branch_admin" ? (user?.name || "") : "");
  const ownerName = school?.owner_name || (isOwnerPrinting ? (user?.name || "") : "");

  const doPrint = () => { window.print(); };

  return createPortal(
    <div className="ovw-pp-overlay" id="ovw-print-portal" data-testid="print-preview-overlay">
      {/* Toolbar — hidden in print */}
      <div className="ovw-pp-toolbar no-print">
        <div className="ovw-pp-toolbar-title">
          Print Preview · A4 Landscape
        </div>
        <div className="ovw-pp-toolbar-actions">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-2xl border-brand-purple/30 text-foreground hover:bg-brand-purpleLight h-10 px-5"
            data-testid="print-preview-cancel"
          >
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={doPrint}
            className="rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white h-10 px-5"
            data-testid="print-preview-print"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* The A4 landscape page itself */}
      <div className="ovw-pp-scroll">
        <div id="ovw-print-page" className="ovw-print-page" data-testid="print-page">
          <header className="ovw-print-header">
            {school?.logo_base64 ? (
              <img src={school.logo_base64} alt="" className="ovw-print-logo" />
            ) : (
              <div className="ovw-print-logo-fallback" aria-hidden />
            )}
            <div className="ovw-print-titles">
              <div className="ovw-print-school">{school?.name || ""}</div>
              <div className="ovw-print-branch">{branch?.name || ""}</div>
              <div className="ovw-print-title">PAY SLIP OVERVIEW</div>
              <div className="ovw-print-period">
                {monthName} {year} · {periodLabel}
              </div>
              <div className="ovw-print-covered">Covered Dates: {coveredDates}</div>
            </div>
          </header>

          <table className="ovw-print-table">
            <thead>
              <tr>
                <th rowSpan={2} className="ovw-pt-name">EMPLOYEE NAME</th>
                {data.income_columns.map((col) => (
                  <th key={col.id} colSpan={incomeColSpan(col)} className="ovw-pt-grp ovw-pt-income">
                    <div>{col.name?.toUpperCase()}</div>
                  </th>
                ))}
                <th rowSpan={2} className="ovw-pt-leaf ovw-pt-income">
                  <div>OVERTIME PAY</div>
                </th>
                {data.deduction_labels.length > 0 && (
                  <th colSpan={data.deduction_labels.length} className="ovw-pt-grp ovw-pt-gov">
                    GOV. DEDUCTIONS
                  </th>
                )}
                {data.other_deduction_names.length > 0 && (
                  <th colSpan={data.other_deduction_names.length} className="ovw-pt-grp ovw-pt-other">
                    OTHER DEDUCTIONS
                  </th>
                )}
                <th rowSpan={2} className="ovw-pt-leaf ovw-pt-gross">GROSS PAY</th>
                <th rowSpan={2} className="ovw-pt-leaf ovw-pt-net">NET PAY</th>
                <th rowSpan={2} className="ovw-pt-leaf ovw-pt-sigcol">SIGNATURE</th>
              </tr>
              <tr>
                {data.income_columns.map((col) => (
                  isExpanded(col) ? (
                    <React.Fragment key={`pp-${col.id}-sub`}>
                      <th className="ovw-pt-sub">Normal</th>
                      <th className="ovw-pt-sub">Reg. Holiday</th>
                      <th className="ovw-pt-sub">Spec. Holiday</th>
                    </React.Fragment>
                  ) : (
                    <th key={`pp-${col.id}-sub`} className="ovw-pt-sub">Amount</th>
                  )
                ))}
                {data.deduction_labels.map((l) => (
                  <th key={`pp-d-${l.id}`} className="ovw-pt-sub">{l.name?.toUpperCase()}</th>
                ))}
                {data.other_deduction_names.map((n) => (
                  <th key={`pp-o-${n}`} className="ovw-pt-sub">{n?.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={`pp-row-${r.employee_id}`}>
                  <td className="ovw-pt-td-name"><div className="ovw-pt-name-clamp">{r.employee_name}</div></td>
                  {data.income_columns.map((col) => {
                    const v = r.by_label[col.id];
                    if (isExpanded(col)) {
                      return (
                        <React.Fragment key={`pp-c-${r.employee_id}-${col.id}`}>
                          <td className="ovw-pt-td">{fmt(v?.normal_working_day)}</td>
                          <td className="ovw-pt-td">{fmt(v?.regular_holiday)}</td>
                          <td className="ovw-pt-td">{fmt(v?.special_holiday)}</td>
                        </React.Fragment>
                      );
                    }
                    return <td key={`pp-c-${r.employee_id}-${col.id}`} className="ovw-pt-td">{fmt(incomeCellValue(col, v))}</td>;
                  })}
                  <td className="ovw-pt-td">{fmt(r.overtime_total)}</td>
                  {data.deduction_labels.map((l) => (
                    <td key={`pp-rd-${r.employee_id}-${l.id}`} className="ovw-pt-td">{fmt(r.by_deduction[l.id])}</td>
                  ))}
                  {data.other_deduction_names.map((n) => (
                    <td key={`pp-ro-${r.employee_id}-${n}`} className="ovw-pt-td">{fmt(r.by_other_deduction?.[n])}</td>
                  ))}
                  <td className="ovw-pt-td ovw-pt-td-gross">{fmt(r.gross_pay)}</td>
                  <td className="ovw-pt-td ovw-pt-td-net">{fmt(r.net_pay)}</td>
                  <td className="ovw-pt-td ovw-pt-td-sig">&nbsp;</td>
                </tr>
              ))}
              {totals && (
                <tr className="ovw-pt-foot">
                  <td className="ovw-pt-td-name">GRAND TOTAL</td>
                  {data.income_columns.map((col) => {
                    const v = totals.by_label[col.id];
                    if (isExpanded(col)) {
                      return (
                        <React.Fragment key={`pp-tot-${col.id}`}>
                          <td className="ovw-pt-td">{fmt(v?.normal_working_day)}</td>
                          <td className="ovw-pt-td">{fmt(v?.regular_holiday)}</td>
                          <td className="ovw-pt-td">{fmt(v?.special_holiday)}</td>
                        </React.Fragment>
                      );
                    }
                    return <td key={`pp-tot-${col.id}`} className="ovw-pt-td">{fmt(incomeCellValue(col, v))}</td>;
                  })}
                  <td className="ovw-pt-td">{fmt(totals.overtime_total)}</td>
                  {data.deduction_labels.map((l) => (
                    <td key={`pp-totd-${l.id}`} className="ovw-pt-td">{fmt(totals.by_deduction[l.id])}</td>
                  ))}
                  {data.other_deduction_names.map((n) => (
                    <td key={`pp-toto-${n}`} className="ovw-pt-td">{fmt(totals.by_other_deduction[n])}</td>
                  ))}
                  <td className="ovw-pt-td ovw-pt-td-gross">{fmt(totals.gross_pay)}</td>
                  <td className="ovw-pt-td ovw-pt-td-net">{fmt(totals.net_pay)}</td>
                  <td className="ovw-pt-td">&nbsp;</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Signature footer — 5 row-heights below the table */}
          <div className="ovw-print-sig-wrap">
            <div className="ovw-print-sig">
              <div className="ovw-print-sig-name">{branchAdminName || "\u00A0"}</div>
              <div className="ovw-print-sig-line" />
              <div className="ovw-print-sig-title">BRANCH ADMIN</div>
            </div>
            <div className="ovw-print-sig">
              <div className="ovw-print-sig-name">{ownerName || "\u00A0"}</div>
              <div className="ovw-print-sig-line" />
              <div className="ovw-print-sig-title">SCHOOL OWNER</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ovw-pp-overlay {
          position: fixed; inset: 0; z-index: 60;
          background: rgba(15, 23, 42, 0.55);
          display: flex; flex-direction: column;
        }
        .ovw-pp-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 1rem; padding: 0.75rem 1.25rem;
          background: #fff;
          border-bottom: 1px solid rgba(37, 99, 235, 0.12);
          box-shadow: 0 4px 12px -8px rgba(15, 23, 42, 0.25);
        }
        .ovw-pp-toolbar-title {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 0.95rem; color: #0f172a;
        }
        .ovw-pp-toolbar-actions { display: flex; gap: 0.5rem; }
        .ovw-pp-scroll {
          flex: 1; overflow: auto; padding: 1.5rem; display: flex; justify-content: center;
        }
        .ovw-print-page {
          /* Landscape A4 minus margins: 297mm × 210mm, content area ≈ 277×190 */
          width: 277mm; min-height: 190mm; background: #fff;
          padding: 4mm 10mm 8mm 10mm;
          box-shadow: 0 8px 32px -8px rgba(15, 23, 42, 0.35);
          color: #0f172a;
          font-family: var(--font-body), sans-serif;
        }
        .ovw-print-header {
          display: flex; align-items: center; justify-content: center;
          gap: 14mm; margin-bottom: 4mm; text-align: center;
          border-bottom: 1.5pt solid #0f172a; padding-bottom: 3mm;
        }
        .ovw-print-logo {
          width: 22mm; height: 22mm; object-fit: contain; flex-shrink: 0;
        }
        .ovw-print-logo-fallback {
          width: 22mm; height: 22mm; flex-shrink: 0;
          background: linear-gradient(135deg, #DBEAFE, #BFDBFE);
          border-radius: 6mm;
        }
        .ovw-print-titles { display: flex; flex-direction: column; gap: 1mm; }
        .ovw-print-school {
          font-family: var(--font-heading), inherit;
          font-weight: 800; font-size: 18pt; letter-spacing: 0.02em;
          color: #0f172a; text-transform: uppercase;
        }
        .ovw-print-branch {
          font-family: var(--font-heading), inherit;
          font-weight: 600; font-size: 12pt; color: #334155;
        }
        .ovw-print-title {
          font-family: var(--font-heading), inherit;
          font-weight: 800; font-size: 14pt; letter-spacing: 0.08em;
          color: #2563eb; margin-top: 1.5mm; text-transform: uppercase;
        }
        .ovw-print-period {
          font-size: 10pt; font-weight: 600; color: #0f172a; margin-top: 1mm;
        }
        .ovw-print-covered { font-size: 9pt; color: #475569; }

        .ovw-print-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 7.5pt;
          font-variant-numeric: tabular-nums;
          table-layout: auto;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .ovw-print-table th, .ovw-print-table td {
          border: 0.75pt solid #475569;
          padding: 1.2mm 1.5mm;
          text-align: center;
          vertical-align: middle;
          white-space: nowrap;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .ovw-print-table thead th {
          background: #EEF3FF;
          font-family: var(--font-heading), inherit;
          font-weight: 700;
          font-size: 7pt;
          color: #0f172a;
          letter-spacing: 0.02em;
          border: 0.75pt solid #475569;
        }
        .ovw-pt-grp-sub {
          font-weight: 400; font-size: 6pt; color: #64748b;
          text-transform: none; margin-top: 0.5mm;
        }
        .ovw-pt-income { color: #047857; }
        .ovw-pt-gov { color: #b91c1c; }
        .ovw-pt-other { color: #2563eb; }
        .ovw-pt-gross { color: #0f172a; }
        .ovw-pt-net { color: #2563eb; }
        .ovw-pt-sigcol { color: #0f172a; min-width: 32mm; }
        .ovw-pt-td-sig {
          /* Empty cell — let the employee hand-sign right on the printout. */
          min-width: 32mm; height: 8mm;
        }
        .ovw-pt-name, .ovw-pt-td-name {
          text-align: left !important;
          font-weight: 700;
          font-size: 7.5pt;
          text-transform: uppercase;
          letter-spacing: 0.01em;
          min-width: 34mm;
          max-width: 44mm;
          white-space: normal !important;
          word-break: break-word;
          overflow-wrap: anywhere;
          line-height: 1.15;
          vertical-align: middle;
        }
        /* Inner wrapper to cap the name at 2 visible rows without breaking the
           table cell layout itself. */
        .ovw-pt-name-clamp {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-height: 2.4em; /* fallback for non-webkit print engines */
        }
        .ovw-pt-sub {
          font-size: 6.5pt;
          font-weight: 600;
          color: #475569;
          background: #F6F8FE;
        }
        .ovw-pt-td { font-size: 7.5pt; color: #0f172a; }
        .ovw-pt-td-gross { font-weight: 700; }
        .ovw-pt-td-net { font-weight: 700; color: #2563eb; }
        .ovw-pt-foot td {
          background: #E2EAFB;
          font-family: var(--font-heading), inherit;
          font-weight: 700;
          border-top: 1pt solid #475569;
        }

        .ovw-print-sig-wrap {
          display: flex; justify-content: space-between;
          margin-top: 28mm; /* about 5 row-heights of empty space */
          padding: 0 8mm;
          gap: 16mm;
        }
        .ovw-print-sig {
          flex: 1; max-width: 90mm; text-align: center;
        }
        .ovw-print-sig-name {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 10pt;
          padding: 0 2mm 1mm 2mm;
          text-transform: uppercase;
          min-height: 5mm;
        }
        .ovw-print-sig-line {
          border-top: 0.75pt solid #0f172a; margin: 0 4mm;
        }
        .ovw-print-sig-title {
          font-family: var(--font-heading), inherit;
          font-weight: 600; font-size: 8.5pt;
          letter-spacing: 0.08em;
          color: #475569; margin-top: 1.2mm;
          text-transform: uppercase;
        }

        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          /* The overlay is portaled into <body> as #ovw-print-portal. Hide
             every other body child so the (hidden) app shell doesn't take
             layout space and push the printout onto blank extra pages. */
          body > *:not(#ovw-print-portal) { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; height: auto !important; }
          #ovw-print-portal {
            position: static !important;
            background: #fff !important;
            display: block !important;
            inset: auto !important;
            z-index: auto !important;
          }
          .no-print { display: none !important; }
          .ovw-pp-overlay {
            position: static !important;
            background: #fff !important;
            display: block !important;
          }
          .ovw-pp-scroll {
            padding: 0 !important; overflow: visible !important;
            display: block !important; height: auto !important;
          }
          .ovw-print-page {
            width: auto !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .ovw-print-table {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .ovw-print-table th, .ovw-print-table td {
            border: 0.75pt solid #475569 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .ovw-print-table thead th { background: #EEF3FF !important; }
          .ovw-pt-sub { background: #F6F8FE !important; }
          .ovw-pt-foot td { background: #E2EAFB !important; }
          .ovw-print-table tr { page-break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .ovw-print-sig-wrap { page-break-inside: avoid; margin-top: 14mm !important; }
        }
      `}</style>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-heading text-brand-inkMuted">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3"><AlertCircle className="w-7 h-7 text-[#E65100]" /></div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">The Pay Slip Overview is scoped per branch.</p>
    </div>
  );
}
